import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["name", "ticker", "slug", "message", "submit"];
  static values = {
    url: String,
    excludeId: Number
  };

  connect() {
    this.abortController = null;
    this.timeout = null;
  }

  disconnect() {
    this.abortPending();
    this.clearTimeout();
  }

  queueLookup() {
    this.clearTimeout();
    this.timeout = window.setTimeout(() => this.lookup(), 250);
  }

  async lookup() {
    const params = new URLSearchParams({
      name: this.nameValue,
      ticker: this.tickerValue,
      slug: this.slugValue
    });

    if (!this.shouldLookup(params)) {
      this.renderClear();
      return;
    }

    if (this.hasExcludeIdValue) {
      params.set("exclude_id", this.excludeIdValue);
    }

    this.abortPending();
    this.abortController = new AbortController();

    try {
      const response = await fetch(`${this.urlValue}?${params.toString()}`, {
        headers: { Accept: "application/json" },
        signal: this.abortController.signal
      });

      if (!response.ok) {
        this.renderClear();
        return;
      }

      const data = await response.json();
      if (data.duplicate) {
        this.renderDuplicate(data);
      } else {
        this.renderClear();
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        this.renderClear();
      }
    }
  }

  get nameValue() {
    return this.hasNameTarget ? this.nameTarget.value.trim() : "";
  }

  get tickerValue() {
    return this.hasTickerTarget ? this.tickerTarget.value.trim() : "";
  }

  get slugValue() {
    return this.hasSlugTarget ? this.slugTarget.value.trim() : "";
  }

  shouldLookup(params) {
    const name = params.get("name");
    const ticker = params.get("ticker");
    const slug = params.get("slug");

    return name.length >= 3 || ticker.length >= 2 || slug.length >= 3;
  }

  renderDuplicate(data) {
    if (!this.hasMessageTarget) return;

    const alias = data.submitted_by_alias || "original-submitter";
    const profileLink = data.submitted_by_profile_url
      ? `<a href="${data.submitted_by_profile_url}">@${alias}</a>`
      : `@${alias}`;

    this.messageTarget.hidden = false;
    this.messageTarget.innerHTML =
      `coin already entered as <strong>${data.coin_name} (${data.ticker})</strong>. ` +
      `please contact ${profileLink} before creating another lane.`;

    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true;
      this.submitTarget.setAttribute("aria-disabled", "true");
    }
  }

  renderClear() {
    if (this.hasMessageTarget) {
      this.messageTarget.hidden = true;
      this.messageTarget.innerHTML = "";
    }

    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = false;
      this.submitTarget.removeAttribute("aria-disabled");
    }
  }

  abortPending() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  clearTimeout() {
    if (this.timeout) {
      window.clearTimeout(this.timeout);
      this.timeout = null;
    }
  }
}
